/**
 * Entity media helpers.
 *
 * Central place for deciding what image to show for a partner entity when
 * they haven't uploaded a profile photo. Order of preference:
 *
 *   1. `entity.image_url`               — the operator-uploaded photo
 *   2. Backend link-preview redirect    — /api/link-preview/image?url=...
 *                                        302s to the site's OG image if we've
 *                                        been able to scrape one, otherwise to
 *                                        the shared DEFAULT_ENTITY_IMAGE.
 *   3. DEFAULT_ENTITY_IMAGE             — friendly outdoor stock photo, so
 *                                        cards / pins never render blank.
 *
 * We intentionally do NOT fall back to raw website screenshots (mshots) —
 * screenshots of arbitrary partner homepages are often ugly (cookie banners,
 * loading spinners, blank hero sections) and make the app look broken. A
 * consistent default image is a better user experience.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const FAVICON_BASE = 'https://www.google.com/s2/favicons';

/**
 * Default fallback image shown when a partner has no uploaded photo and no
 * scrapable OG image. Change here to swap the app-wide default in one place.
 * Matches the STOCK_FALLBACK already used by ExperienceCard / DiscoveryMapView.
 */
export const DEFAULT_ENTITY_IMAGE =
  'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800&q=80&auto=format&fit=crop';

// Only http/https website values are safe to hand off to the preview service.
// Anything else (empty, mailto:, tel:, malformed) means "no scrape target
// available" and we skip straight to the default image.
const _normalizeWebsite = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  let url = raw.trim();
  if (!url) return null;
  // Common admin input: "example.com" without scheme — force https so the
  // OG scraper can fetch it.
  if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.href;
  } catch {
    return null;
  }
};

/**
 * Build a URL to our backend's link-preview redirect. The backend prefers the
 * scraped OG image and falls back to the shared default — from the frontend's
 * perspective it's just an <img src>, no async fetch required.
 */
export const linkPreviewImageUrl = (website, width = 800) => {
  const normalized = _normalizeWebsite(website);
  if (!normalized || !API_BASE) return null;
  const params = new URLSearchParams({ url: normalized, w: String(width) });
  return `${API_BASE}/api/link-preview/image?${params.toString()}`;
};

/**
 * Return a Google-hosted favicon URL for a domain. Only used for the small
 * site-icon shown inside <LinkPreviewCard> next to the title — the main
 * image fallback goes through DEFAULT_ENTITY_IMAGE, not the favicon.
 */
export const websiteFaviconUrl = (website, size = 32) => {
  const normalized = _normalizeWebsite(website);
  if (!normalized) return null;
  try {
    const domain = new URL(normalized).hostname;
    return `${FAVICON_BASE}?domain=${encodeURIComponent(domain)}&sz=${size}`;
  } catch {
    return null;
  }
};

/**
 * Pick the best available image URL for an entity. Falls back through the
 * order documented at the top of this file, always returning a non-null
 * value so cards never render blank.
 */
export const getEntityImageUrl = (entity, { width = 800 } = {}) => {
  if (!entity) return DEFAULT_ENTITY_IMAGE;
  if (entity.image_url) return entity.image_url;
  const preview = linkPreviewImageUrl(entity.website, width);
  if (preview) return preview;
  return DEFAULT_ENTITY_IMAGE;
};

/**
 * onError handler for <img> tags — swap to the shared default image on the
 * first failure, then hide the <img> entirely if the default itself also
 * fails (extremely unlikely). Attach with:
 *
 *   <img src={getEntityImageUrl(entity)} onError={makeImageFallback(entity)} />
 *
 * Callers who want a different fallback (e.g. a hero image for a full-page
 * layout instead of a card-sized one) can pass `fallbackSrc`.
 */
export const makeImageFallback = (entity, fallbackSrc = null) => (event) => {
  const img = event.currentTarget;
  if (!img) return;

  // Track fallback state on the element itself so a re-render doesn't reset it.
  const step = img.dataset.fbStep || '0';

  if (step === '0') {
    const nextSrc = fallbackSrc ?? DEFAULT_ENTITY_IMAGE;
    if (nextSrc && img.src !== nextSrc) {
      img.dataset.fbStep = '1';
      img.src = nextSrc;
      return;
    }
  }

  // Default image also failed — collapse cleanly instead of showing broken UI.
  img.onerror = null;
  img.style.display = 'none';
};
